import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import type { Event, Client } from '@planner/types'

interface EventWithClient extends Event {
  clients?: Client
}

export function useEvent(id: string | undefined) {
  const [event, setEvent] = useState<EventWithClient | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [isFetching, setIsFetching] = useState(false)

  const fetchEvent = useCallback(async () => {
    if (!id || id === 'placeholder') {
      setLoading(false)
      setEvent(null)
      return
    }

    // Validate UUID format
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    if (!uuidRegex.test(id)) {
      console.error('Invalid event ID format:', id)
      setError(new Error('Nieprawidłowy format ID wydarzenia'))
      setEvent(null)
      setLoading(false)
      setIsFetching(false)
      return
    }

    setIsFetching(true)
    setError(null)

    try {
      console.log('Fetching event with ID:', id)
      const { data, error: fetchError } = await supabase
        .from('events')
        .select(`
          *,
          clients!events_client_id_fkey (
            id,
            full_name,
            email,
            phone,
            company,
            address,
            client_contacts (*)
          )
        `)
        .eq('id', id)
        .single()

      if (fetchError) {
        console.error('Error fetching event:', fetchError)
        // Check if it's a "not found" error
        if (fetchError.code === 'PGRST116') {
          setError(new Error('Wydarzenie nie zostało znalezione'))
        } else {
          setError(new Error(fetchError.message || 'Nie udało się załadować wydarzenia'))
        }
        setEvent(null)
        setLoading(false)
      } else {
        console.log('Event fetched successfully:', data)
        setEvent(data || null)
        setLoading(false)
      }
    } catch (err: unknown) {
      console.error('Exception while fetching event:', err)
      setError(err instanceof Error ? err : new Error('Nieoczekiwany błąd podczas ładowania wydarzenia'))
      setEvent(null)
      setLoading(false)
    } finally {
      setIsFetching(false)
    }
  }, [id])

  useEffect(() => {
    fetchEvent()
  }, [fetchEvent])

  // loading = true tylko gdy faktycznie fetchujemy i nie mamy danych
  // Ale tylko jeśli id jest dostępne (query powinno się wykonać)
  const isLoading = !!id && id !== 'placeholder' && isFetching && !event

  return { event, loading: isLoading, error, isFetching }
}
